import { hierarchy, HierarchyPointNode, tree } from "d3-hierarchy";
import { Edge, getIncomers, getOutgoers, Node } from "reactflow";
import { NodeType } from "./FlowEditor";
import { NodeData } from "./Nodes/NodeData";

export interface HierarchyObject {
  id: string;
  children?: HierarchyObject[];
}

export const retrieveHierarchyObject = (
  root: Node,
  nodes: Node[],
  edges: Edge[]
): HierarchyObject => {
  const children = getOutgoers(root, nodes, edges);

  return {
    id: root.id,
    ...(children.length > 0
      ? {
          children: children.map((child) =>
            retrieveHierarchyObject(child, nodes, edges)
          ),
        }
      : {}),
  };
};

export const retrieveNodesFromHierarchyObject = (
  object: HierarchyPointNode<HierarchyObject>,
  nodes: Node[]
): Node[] => {
  const {
    data: { id },
    x,
    y,
    children,
  } = object;

  const nodeToChangeIndex = nodes.findIndex((node) => node.id === id);
  const nodeToChange = nodes[nodeToChangeIndex];

  if (!nodeToChange) return [];
  const newNodes = [...nodes];

  const newNode: Node<NodeData> = {
    ...nodeToChange,
    position: { x, y },
  };

  newNodes[nodeToChangeIndex] = newNode;

  if (!children) return [newNode];

  const anotherNodes = children.reduce<Node<NodeData>[]>(
    (acc, item) => [
      ...acc,
      ...retrieveNodesFromHierarchyObject(item, newNodes),
    ],
    []
  );

  return [newNode, ...anotherNodes];
};

export const translateTree = (
  treeRoot: Node,
  nodes: Node[],
  edges: Edge[],
  offset: { x: number; y: number }
) => {
  treeRoot.position.x += offset.x;
  treeRoot.position.y += offset.y;

  const outgoers = getOutgoers(treeRoot, nodes, edges);

  for (const child of outgoers) {
    translateTree(child, nodes, edges, offset);
  }
};

const NODE_WIDTH = 260;
const HORIZONTAL_OFFSET = 45;

const nodeTypesWithPossibleStats: string[] = [
  NodeType.MESSAGE,
  NodeType.TRACKER,
];

const nodeTypeHeightMap: Record<NodeType, number> = {
  [NodeType.EMPTY]: 80,
  [NodeType.EXIT]: 60,
  [NodeType.INSERT_NODE]: 80,
  [NodeType.JUMP_TO]: 60,
  [NodeType.MESSAGE]: 80,
  [NodeType.START]: 60,
  [NodeType.TIME_DELAY]: 80,
  [NodeType.TIME_WINDOW]: 80,
  [NodeType.TRACKER]: 80,
  [NodeType.USER_ATTRIBUTE]: 80,
  [NodeType.WAIT_UNTIL]: 80,
  [NodeType.MULTISPLIT]: 80,
  [NodeType.EXPERIMENT]: 80,
  [NodeType.PUSH]: 80,
  [NodeType.AI_RESPONSE]: 100,
  [NodeType.RESPONSE_BRANCH]: 80,
  [NodeType.RICOCHET]: 88,
};

const STATS_HEIGHT = 60;
const VERTICAL_GAP_BETWEEN_NODES = 88;

// Measure the horizontal half-extents of a subtree relative to the root's
// center: how far left the leftmost descendant's left edge sits, and how
// far right the rightmost descendant's right edge sits. Used by the
// Response Branch tight-pull to avoid overlapping siblings whose subtrees
// are wider than a single column.
const computeSubtreeHorizontalBounds = (
  root: Node,
  nodes: Node[],
  edges: Edge[]
): { leftHalf: number; rightHalf: number } => {
  let minLeft = root.position.x - NODE_WIDTH / 2;
  let maxRight = root.position.x + NODE_WIDTH / 2;
  const visit = (n: Node) => {
    const left = n.position.x - NODE_WIDTH / 2;
    const right = n.position.x + NODE_WIDTH / 2;
    if (left < minLeft) minLeft = left;
    if (right > maxRight) maxRight = right;
    for (const c of getOutgoers(n, nodes, edges)) visit(c);
  };
  visit(root);
  return {
    leftHalf: root.position.x - minLeft,
    rightHalf: maxRight - root.position.x,
  };
};

export const applyLayoutCorrections = (
  rootNode: Node,
  nodes: Node[],
  edges: Edge[]
) => {
  const children = getOutgoers(rootNode, nodes, edges);

  const rootHeight = nodeTypeHeightMap[rootNode.type as NodeType] || 0;

  const isResponseBranchParent = rootNode.type === NodeType.RESPONSE_BRANCH;

  for (const child of children) {
    const childHeight =
      (nodeTypeHeightMap[child.type as NodeType] || 0) +
      (child.data.stats ? STATS_HEIGHT : 0);

    translateTree(child, nodes, edges, {
      x: 0,
      y:
        (rootHeight + childHeight) / 2 +
        VERTICAL_GAP_BETWEEN_NODES +
        (children.length > 1 && !isResponseBranchParent
          ? 2 * VERTICAL_GAP_BETWEEN_NODES
          : 0),
    });

    const childOutgoers = getOutgoers(child, nodes, edges);

    if (
      child.type &&
      child.data.stats &&
      childOutgoers.length === 1 &&
      nodeTypesWithPossibleStats.includes(child.type)
    ) {
      translateTree(childOutgoers[0], nodes, edges, {
        x: 0,
        y: STATS_HEIGHT / 2,
      });
    }

    applyLayoutCorrections(child, nodes, edges);
  }

  // Pull a Response Branch's immediate children horizontally tight AFTER
  // each child's subtree has finished laying out (post-order). Spacing is
  // computed from the actual measured extent of each child's subtree, so
  // a child that is itself a branching node — and therefore wider than a
  // single column — no longer slides under its neighbor.
  //
  // Order: regular reply branches (left-to-right by current x), then the
  // optional No-reply timeout branch, then the "Any other reply" catch-all
  // pinned to the rightmost slot.
  if (isResponseBranchParent && children.length > 1) {
    const branchOf = (child: Node) => {
      const edge = edges.find(
        (e) => e.source === rootNode.id && e.target === child.id
      );
      return (edge?.data as any)?.branch;
    };
    const isCatchAll = (child: Node) => branchOf(child)?.isOthers === true;
    const isTimeout = (child: Node) => branchOf(child)?.isTimeout === true;

    const regular = children
      .filter((c) => !isCatchAll(c) && !isTimeout(c))
      .sort((a, b) => a.position.x - b.position.x);
    const timeout = children.filter(isTimeout);
    const catchall = children.filter(isCatchAll);
    const sortedChildren = [...regular, ...timeout, ...catchall];

    const bounds = sortedChildren.map((c) =>
      computeSubtreeHorizontalBounds(c, nodes, edges)
    );

    // Place left-to-right so each subtree's right edge sits one
    // VERTICAL_GAP_BETWEEN_NODES away from the next subtree's left edge.
    const relativeX: number[] = [0];
    for (let i = 1; i < sortedChildren.length; i++) {
      relativeX.push(
        relativeX[i - 1] +
          bounds[i - 1].rightHalf +
          bounds[i].leftHalf +
          VERTICAL_GAP_BETWEEN_NODES
      );
    }

    const totalWidth = relativeX[relativeX.length - 1] - relativeX[0];
    const startX = rootNode.position.x - totalWidth / 2;
    sortedChildren.forEach((child, i) => {
      const desiredX = startX + relativeX[i];
      const dx = desiredX - child.position.x;
      if (dx !== 0) {
        translateTree(child, nodes, edges, { x: dx, y: 0 });
      }
    });
  }
};

export const getLayoutedNodes = (nodes: Node[], edges: Edge[]) => {
  let rootNode = nodes.find(
    (node) =>
      getIncomers(node, nodes, edges).length === 0 &&
      node.type === NodeType.START
  );
  if (!rootNode) return nodes;

  const root = hierarchy(retrieveHierarchyObject(rootNode, nodes, edges));
  const treeLayout = tree<HierarchyObject>()
    .nodeSize([260, 0])
    .separation(() => 2);

  const layoutedNodes = treeLayout(root);

  nodes = retrieveNodesFromHierarchyObject(layoutedNodes, nodes);

  rootNode = nodes.find(
    (node) =>
      getIncomers(node, nodes, edges).length === 0 &&
      node.type === NodeType.START
  );
  if (!rootNode) return nodes;

  applyLayoutCorrections(rootNode, nodes, edges);

  return nodes;
};
